import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Kontakter } from "~app/common/models/constants";
import { KontaktDTO } from "~app/common/models/kontakter/KontaktDTO";
import { MomsnummerValideringsstatus } from "~app/common/models/momsnummerValideringsstatus";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationRuleSettings } from "~app/common/validation/IValidationRuleSettings";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ValidationContext } from "../../../../common/validation/validationContext";

@Injectable()
export class LeverantorValidation {

    private static readonly juridiskPerson = "Juridisk person";
    private static readonly privatPerson = "Privatperson";

    static init(translateService: TranslateService, kontakt: KontaktDTO): ValidationContext {
        const context = new ValidationContext();

        context.create(ValidationScopes.spara)
            .withRulesWhen(() => kontakt.kontaktLimeId == null || kontakt.kontaktLimeId.length === 0,
                this.createNotLimeRules(translateService, kontakt).concat(this.createMaxLengthRules(kontakt))
            )
            .withRulesWhen(() => kontakt.kontaktLimeId != null && kontakt.kontaktLimeId.length > 0,
                this.createLimeRules(translateService, kontakt).concat(this.createMaxLengthRules(kontakt))
            );
        context.create(ValidationScopes.skapa)
            .withRules(this.createLimeRules(translateService, kontakt).concat(this.createMaxLengthRules(kontakt)));

        return context;
    }

    private static createMaxLengthRules(kontakt: KontaktDTO): IValidationRuleSettings[] {
        return [

            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktNamn",
                expression: (newValue: string) => isValidLength(newValue, 25),
                message: { text: maxLengthMessage("Leverantör", 25) },
                model: () => kontakt.kontaktNamn,
                name: "kontaktNamn"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktTelefon",
                expression: (newValue: string) => isValidLength(newValue, 12),
                message: { text: maxLengthMessage("Telefonnummer", 12) },
                model: () => kontakt.kontaktTelefon,
                name: "kontaktTelefon"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktKontaktMobiltelefon",
                expression: (newValue: string) => isValidLength(newValue, 12),
                message: { text: maxLengthMessage("Mobiltelefonnummer", 12) },
                model: () => kontakt.kontaktKontaktMobiltelefon,
                name: "kontaktKontaktMobiltelefon"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktAdress",
                expression: (newValue: string) => isValidLength(newValue, 25),
                message: { text: maxLengthMessage("Adress 1", 25) },
                model: () => kontakt.kontaktAdress,
                name: "kontaktAdress"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktAdress2",
                expression: (newValue: string) => isValidLength(newValue, 25),
                message: { text: maxLengthMessage("Adress 2", 25) },
                model: () => kontakt.kontaktAdress2,
                name: "kontaktAdress2"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktPostnummer",
                expression: (newValue: string) => isValidLength(newValue, 6),
                message: { text: maxLengthMessage("Postnummer", 6) },
                model: () => kontakt.kontaktPostnummer,
                name: "kontaktPostnr"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktPostort",
                expression: (newValue: string) => isValidLength(newValue, 20),
                message: { text: maxLengthMessage("Postort", 20) },
                model: () => kontakt.kontaktPostort,
                name: "kontaktOrt"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: "Postort måste anges" },
                model: () => kontakt.kontaktPostort,
                required: true,
                name: "kontaktOrtReq"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktEpost",
                expression: (newValue: string) => isValidLength(newValue, 255),
                message: { text: maxLengthMessage("E-post", 255) },
                model: () => kontakt.kontaktKontaktEpost,
                name: "kontaktEpost"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktEpostFormat",
                applicable: () => !!kontakt.kontaktKontaktEpost,
                expression: (newValue: string) => !newValue || new RegExp(Kontakter.EpostRegularExpression).test(newValue),
                message: { text: "E-post har ett ogiltigt format" },
                model: () => kontakt.kontaktKontaktEpost,
                name: "kontaktEpostFormat"
            },
            {
                errorLevel: ValidationErrorLevels.warning,
                id: "kontaktMomsregFelEjValiderad",
                applicable: () => (kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.ejValiderad),
                expression: (newValue: string) => false,
                message: { text: "Under senaste kontroll av momsregnr gick inte VIES-tjänsten att nå, vänligen försök igen." },
                model: () => kontakt.momsregistreringsnummer,
                name: "kontaktMomsregStatusFelEjValiderad",
                triggerOnLoad: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktMomsregPleaseCheck",
                applicable: () => true,
                trueExpression: (newValue: string) => newValue && newValue !== kontakt.momsnummerKontrollerat,
                message: { text: "Momsregistreringsnumret måste kontrolleras innan det kan sparas. Rensa om du vill spara ändå." },
                model: () => kontakt.momsregistreringsnummer,
                name: "kontaktMomsregStatusPleaseCheck"
            },
            {
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktMomsregOgiltig",
                applicable: () => true,
                trueExpression: (newValue: string) => kontakt.momsregistreringsnummer &&
                    kontakt.momsnummerValideringsstatus === MomsnummerValideringsstatus.ogiltig &&
                    (newValue && newValue === kontakt.momsnummerKontrollerat),
                message: { text: "Momsregistreringsnumret som validerades saknas i VIES databas och är därmed inte giltigt. Rensa om du vill spara ändå." },
                model: () => kontakt.momsregistreringsnummer,
                name: "kontaktMomsregStatusOgiltig",
                triggerOnLoad: true,
                publish: true,
                verbose: true
            },
            {
                errorLevel: ValidationErrorLevels.warning,
                id: "kontaktMomsregFormat",
                applicable: () => {
                    if (kontakt.momsregistreringsnummer) {
                        // eslint-disable-next-line sonarjs/concise-regex
                        const regexp = new RegExp("^SE[0-9]{12}$");
                        return !regexp.test(kontakt.momsregistreringsnummer);
                    }
                    return false;
                },
                expression: (newValue: string) => false,
                message: { text: "Momsregistreringsnumret har fel format för att kunna valideras. (SEnnnnnnnnnn01)" },
                model: () => kontakt.momsregistreringsnummer,
                name: "kontaktMomsregFormat",
                triggerOnLoad: true
            }
        ];
    }

    private static createLimeRules(translateService: TranslateService, kontakt: KontaktDTO): IValidationRuleSettings[] {

        return [
            {
                applicable: () => kontakt.typAvKontakt === LeverantorValidation.juridiskPerson,
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktNamn",
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTNAMN") },
                model: () => kontakt.kontaktNamn,
                name: "kontaktNamn",
                required: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTPERSONFORNAMN") },
                model: () => kontakt.kontaktpersonFornamn,
                name: "kontaktpersonFornamn",
                required: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTPERSONEFTERNAMN") },
                model: () => kontakt.kontaktpersonEfternamn,
                name: "kontaktpersonEfternamn",
                required: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTORGNR") },
                model: () => kontakt.kontaktOrgnummer,
                name: "kontaktOrgnr",
                required: true
            },
            {
                applicable: () => kontakt.typAvKontakt === LeverantorValidation.juridiskPerson && kontakt.kontaktOrgnummer && kontakt.kontaktOrgnummer.length > 0,
                errorLevel: ValidationErrorLevels.error,
                expression: orgnummer => {
                    if (!orgnummer || orgnummer.length === 0) {
                        return true;
                    }
                    const regexp = new RegExp(Kontakter.OrgnummerRegularExpression);
                    return regexp.test(orgnummer);
                },
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTORGNRFORMAT") },
                model: () => kontakt.kontaktOrgnummer,
                name: "kontaktOrgnrFormat"
            },
            {
                applicable: () => kontakt.typAvKontakt === LeverantorValidation.privatPerson && kontakt.kontaktOrgnummer && kontakt.kontaktOrgnummer.length > 0,
                errorLevel: ValidationErrorLevels.error,
                expression: personnummer => {
                    if (!personnummer || personnummer.length === 0) {
                        return true;
                    }
                    const regexp = new RegExp(Kontakter.PersnummerRegularExpression);
                    return regexp.test(personnummer);
                },
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTPNRFORMAT") },
                model: () => kontakt.kontaktOrgnummer,
                name: "kontaktPnrFormat"
            },
            {
                applicable: () => kontakt.kontaktAdress2 == null || kontakt.kontaktAdress2.length === 0,
                errorLevel: ValidationErrorLevels.warning,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_ADRESS") },
                model: () => kontakt.kontaktAdress,
                name: "kontaktAdress",
                required: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTPOSTNR") },
                model: () => kontakt.kontaktPostnummer,
                name: "kontaktPostnr",
                required: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTORT") },
                model: () => kontakt.kontaktPostort,
                name: "kontaktOrt",
                required: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTLAND") },
                model: () => kontakt.kontaktLand,
                name: "kontaktLand",
                required: true
            }
        ];
    }
    private static createNotLimeRules(translateService: TranslateService, kontakt: KontaktDTO): IValidationRuleSettings[] {
        return [
            {
                applicable: () => kontakt.typAvKontakt === LeverantorValidation.juridiskPerson,
                errorLevel: ValidationErrorLevels.error,
                id: "kontaktNamn",
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_KONTAKTNAMN") },
                model: () => kontakt.kontaktNamn,
                name: "kontaktNamn",
                required: true
            },
            {
                errorLevel: ValidationErrorLevels.error,
                message: { text: translateService.instant("LEVERANTOR_VALIDERING_TYPAVKONTAKT") },
                model: () => kontakt.typAvKontakt,
                name: "typAvKontakt",
                required: true
            }
        ];
    }
}

const isValidLength = (input: string, maxAllowed: number): boolean => {
    if (ObjectUtils.isNullOrUndefined(input)) {
        return true;
    }

    return input.length <= maxAllowed;
};

const isValidClearingnummmer = (input: string): boolean => {
    if (ObjectUtils.isNullOrUndefined(input) || input === "") {
        return true;
    }

    const isNum = /^\d+$/.test(input);

    return (isNum && input.length === 4);
};

const maxLengthMessage = (propName: string, length: number): string => `${propName} får inte överskrida ${length} tecken`;
